import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, Control } from 'react-hook-form';
import { message } from 'antd';
import { UploadFile } from 'antd';
import moment from 'moment';

import SupportTicketApi from 'apis/api/SupportTicket';
import { TTicketPriority } from 'models/SupportTicket';
import { formatCustomerLabel, parseCustomerSearch } from '../utilTicket';
import { EMPTY_LOCATION, ILocationFormValue, toTicketLocation } from '../components/LocationField';
import { RootState } from 'app/store';
import { useSelector } from 'react-redux';

// ============================================================================
// Payload form tạo mới ticket.
// - Name, ContactNumber, CategoryIds, VehiclePlates, Description, Files:
//   map thẳng vào POST /api/SupportTicket/Create (form-data).
// - resolveNote, priority, targetDate, staffFiles: KHÔNG thuộc API Create —
//   đây là phần "xử lý cơ bản ngay lúc tạo" + file của CSKH nhập, nên sau khi
//   Nội dung xử lý + tệp CSKH gửi luôn trong Create (ResolveContent + StaffFiles, có tệp thì bắt buộc nội dung); ưu tiên gọi thêm UpdateTicketByStaff,
//   y hệt saveProgress bên useTicketDetail. mục tiêu để có những xử lý ban đầu cho ng khác biết..
//   tránh khi cần phải vào update lại hoặc lúc ng khác xem sẽ ko biết đã làm đc gì cho phiếu này
// ============================================================================

// models/SupportTicket
export interface IThirdPartyCustomer {
  userName: string;
  systemBranch: string;
  displayName: string;
  phone: string;
}

export interface ICreateTicketForm {
  customerName: string;
  customerUserName: string;
  contactNumber: string;
  systemBranch: string;
  tagIds: number[];
  categoryIds: number[];
  vehiclePlates: string[];
  description?: string;
  files: UploadFile[]; // hình/video khách gửi kèm phiếu (Files trong API Create)
  location: ILocationFormValue; // địa chỉ + tọa độ xe (Location.* trong API Create)

  // xử lý cơ bản ngay lúc tạo — không bắt buộc
  orderCodes: string[];
  resolveNote: string;
  priority: TTicketPriority;
  targetDate: moment.Moment | null;
  staffFiles: UploadFile[]; // file CSKH nhập thêm (không phải Files của API Create)
}

const DEFAULT_VALUES: ICreateTicketForm = {
  customerUserName: '',
  systemBranch: '',
  customerName: '',
  contactNumber: '',
  tagIds: [],
  categoryIds: [],
  vehiclePlates: [],
  description: '',
  files: [],
  location: EMPTY_LOCATION,

  resolveNote: '',
  orderCodes: [],
  priority: 0,
  targetDate: null,
  staffFiles: []
};

interface IUseCreateTicketArgs {
  /** callback sau khi tạo thành công (đóng modal, refresh list, ...) */
  onCreated?: () => void | Promise<void>;
}

export function useCreateTicket({ onCreated }: IUseCreateTicketArgs = {}) {
  const branchFilter = useSelector((state: RootState) => state.select.supportTicketBranch);

  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
    setFocus,
    getValues
  } = useForm<ICreateTicketForm>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit'
  });

  const systemBranch = watch('systemBranch');
  const files = watch('files'); // dữ liệu của khách hàng kèm vào
  const staffFiles = watch('staffFiles'); // cskh nhân viên kèm file

  const buildCreateFormData = (values: ICreateTicketForm) => {
    const formData = new FormData();

    formData.append('customerName', values.customerName.trim());
    formData.append('customerUserName', values.customerUserName.trim());
    formData.append('contactNumber', values.contactNumber.trim());
    formData.append('systemBranch', branchFilterForm.trim());
    formData.append('priority', values.priority + '');

    // truyền luôn vào lúc tạo !!!
    values.targetDate && formData.append('targetDate', values.targetDate?.toISOString(true) ?? null);
    // Nội dung xử lý + tệp CSKH gửi luôn trong Create (server lưu vào resolveContent, phiếu ra "Đang xử lý").
    // Có tệp thì bắt buộc có nội dung (kiểm ở onSubmit).
    const resolveContent = values.resolveNote?.trim();
    if (resolveContent) {
      formData.append('ResolveContent', resolveContent);
      (values.staffFiles || [])
        .filter(f => !!f.originFileObj)
        .forEach(f => formData.append('StaffFiles', f.originFileObj as File));
    }

    (values.categoryIds || []).forEach(id => formData.append('categoryIds', String(id)));
    (values.vehiclePlates || []).forEach(plate => formData.append('vehiclePlates', plate));
    (values.orderCodes || []).forEach(order => formData.append('orderCodes', order));

    if (values.description) formData.append('Description', values.description);

    const location = toTicketLocation(values.location);
    if (location) {
      if (location.address) formData.append('Location.Address', location.address);
      if (location.lat != null && location.lng != null) {
        formData.append('Location.Lat', String(location.lat));
        formData.append('Location.Lng', String(location.lng));
      }
    }

    (values.files || [])
      .filter(f => !!f.originFileObj)
      .forEach(f => formData.append('CustomerFiles', f.originFileObj as File));

    return formData;
  };

  // Sau khi tạo phiếu (có id): ưu tiên khác mặc định -> UpdateTicketByStaff (giữ như cũ).
  // Nội dung + tệp CSKH đã gửi trong Create nên không gọi AddResolveContentByStaff nữa (tránh lưu trùng).
  const saveInitialResolve = async (ticketId: number, values: ICreateTicketForm) => {
    if (values.priority !== 0) {
      const res = await SupportTicketApi.UpdateTicketByStaff({ id: ticketId, priority: values.priority });
      if (res?.data?.error) message.warning('Đã tạo phiếu nhưng lưu mức ưu tiên thất bại!');
    }
  };

  const onSubmit = rhfHandleSubmit(async values => {
    if (!values.categoryIds?.length) {
      message.warning('Vui lòng chọn ít nhất 1 loại yêu cầu!');
      return;
    }

    if (!values.vehiclePlates?.length) {
      message.warning('Vui lòng nhập ít nhất 1 biển số xe!');
      return;
    }

    // Tệp CSKH chỉ gửi kèm nội dung xử lý (ResolveContent trong Create) -> có tệp thì bắt buộc nhập nội dung
    if ((values.staffFiles || []).some(f => !!f.originFileObj) && !values.resolveNote?.trim()) {
      message.warning('Vui lòng nhập nội dung xử lý để gửi kèm tệp CSKH!');
      return;
    }

    setSubmitting(true);
    try {
      const formData = buildCreateFormData(values);
      const res = await SupportTicketApi.CreateTicketBySaff(formData);
      if (!res?.data?.error) {
        const newTicketId = res?.data?.data?.id;
        if (newTicketId) {
          await saveInitialResolve(newTicketId, values);
        }
        message.success('Đã tạo phiếu hỗ trợ mới!');
        reset(DEFAULT_VALUES);
        await onCreated?.();
      } else {
        message.error(res?.data?.serverMessage || res?.data?.message || 'Tạo phiếu hỗ trợ thất bại!');
      }
    } catch (error) {
      console.log('createTicket error', error);
      message.error('Tạo phiếu hỗ trợ thất bại!');
    } finally {
      setSubmitting(false);
    }
  });

  // phần tìm kiếm khách hàng bằng auto complete

  const [customerLoading, setCustomerLoading] = useState(false);

  const [customerList, setCustomerList] = useState<IThirdPartyCustomer[]>([]);
  const [branchFilterForm, setBranchFilterForm] = useState<string | undefined>(branchFilter || null);

  const customerOptions = useMemo(() => {
    // set lại cho form chạy mặc định luôn
    setValue('systemBranch', branchFilter || null); // từ redux synce cho form

    return customerList
      .filter(c => !branchFilter || c.systemBranch === branchFilter) // lọc lại chỉ lấy những khách hàng thuộc branchFilter trên redux
      .map(c => ({ value: c.userName, label: formatCustomerLabel(c) }));
  }, [customerList, branchFilter]);

  const skipSearchRef = useRef(false); // bỏ qua lần search ngay sau khi chọn option
  const searchSeqRef = useRef(0); // chống response cũ ghi đè response mới

  const nameValue = watch('customerName'); // nghe change ô tên khách hàng

  // form chọn lại là tính theo form đồng bộ cuối cho branchFilterForm
  useEffect(() => {
    systemBranch && setBranchFilterForm(systemBranch);
  }, [systemBranch]);

  // Dừng gõ 400ms mới gọi API
  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }

    const { customerName, phoneNum } = parseCustomerSearch(nameValue);

    // console.log('nhập', phoneNum, customerName);

    if (!customerName && !phoneNum) {
      return;
    }

    const seq = ++searchSeqRef.current;
    // delay gọi api parse sẵn khi nhập
    const timer = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const res = await SupportTicketApi.SearchThirdPartyCustomer({
          CustomerName: customerName,
          PhoneNum: phoneNum,
          Size: 50
        });
        if (seq !== searchSeqRef.current) return; // đã có lần search mới hơn

        const list: IThirdPartyCustomer[] = res?.data?.data || [];
        if (seq !== searchSeqRef.current) return;
        setCustomerList(list);
      } catch (e) {
        console.log('searchCustomer error', e);
      } finally {
        if (seq === searchSeqRef.current) setCustomerLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [nameValue]);

  // Chọn khách trong danh sách -> điền name + SĐT (+ lưu account để gửi API tạo phiếu)

  const onSelectCustomer = (userName: string) => {
    const c = customerList.find(x => x.userName === userName);
    if (!c) return;
    skipSearchRef.current = true;
    if (c.phone) setValue('contactNumber', c.phone, { shouldValidate: true });
    setValue('customerUserName', c.userName);
    setValue('customerName', c.displayName);
    setValue('systemBranch', c.systemBranch || null);
  };

  useEffect(() => {
    setFocus('customerName', { shouldSelect: false });
  }, []);

  return {
    control,
    errors,
    submitting,
    files,
    staffFiles,
    onSubmit,
    customerOptions,
    customerLoading,
    onSelectCustomer,
    setValue,
    getValues,
    reset: () => reset(DEFAULT_VALUES)
  };
}

// re-export để component không phải import thêm từ react-hook-form
export type { Control };
export { Controller };
